import { fg } from '@atlaskit/platform-feature-flags/fg';

/**
 * Capability tokens that the backend recognises via the `X-Rovo-Chat-Capabilities`
 * header. Each capability opts the request into a piece of backend behaviour.
 */
export const STORE_MESSAGE_FROM_USER_SETTINGS_CAPABILITY = 'store-message-from-user-settings';
/** Inline actionable follow-ups: questions are woven into prose. */
export const ACTIONABLE_FOLLOW_UPS_CAPABILITY = 'actionable-follow-ups';
/** Stacked actionable follow-ups: questions returned as a plain list, no prose. */
export const ACTIONABLE_FOLLOW_UPS_STACKED_CAPABILITY = 'actionable-follow-ups-stacked';

/**
 * Maps each gated capability to the gate that enables it. Add new
 * capability/gate pairs here rather than introducing nested ternaries at the
 * call sites.
 */
const GATED_CAPABILITIES: ReadonlyArray<{ capability: string; isEnabled: () => boolean }> = [
	{
		capability: STORE_MESSAGE_FROM_USER_SETTINGS_CAPABILITY,
		isEnabled: () => fg('ai_mate_store_messages_be_driven'),
	},
];

/**
 * Builds chat-send capabilities with stacked actionable follow-ups and any
 * gate-enabled capabilities. Legacy inline follow-ups are replaced with stacked
 * follow-ups, and the result is de-duplicated without mutating the caller's list.
 */
export const buildEffectiveCapabilities = (capabilities?: string[]): string[] | undefined => {
	const enabledCapabilities = GATED_CAPABILITIES.filter(({ isEnabled }) => isEnabled()).map(
		({ capability }) => capability,
	);

	enabledCapabilities.push(ACTIONABLE_FOLLOW_UPS_STACKED_CAPABILITY);

	// Older callers may still supply the inline token. Never request both formats.
	const capabilitiesWithoutInlineFollowups = capabilities?.filter(
		(capability) => capability !== ACTIONABLE_FOLLOW_UPS_CAPABILITY,
	);

	return Array.from(
		new Set([...(capabilitiesWithoutInlineFollowups ?? []), ...enabledCapabilities]),
	);
};
